import { useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core';
import TextBoxWithTooltip from '../textBoxWithTooltip';
import StatusIndicator from '../statusIndicator';
import AsyncListBadge from '../asyncListBadge';
import Tag from '../tag';
import { ReactComponent as TagsIcon } from '../../../assets/icons/tag-dark.svg';
import { isArrayEmpty } from '../../../utility/helpers';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    backgroundColor: primary.white,
    boxSizing: 'border-box',
    borderRadius: 16,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    padding: spacing(4),
    width: 270,
    minHeight: 170,
    userSelect: 'none',
  },
  titleWrapper: {
    display: 'flex',
  },
  verticalEllipsis: {
    color: primary.black,
    lineHeight: '24px',
    cursor: 'pointer',
    '-webkit-line-clamp': 2,
  },
  description: {
    marginTop: spacing(1),
    cursor: 'pointer',
    '-webkit-line-clamp': 2,
  },
  coverWrapper: {
    backgroundColor: primary.bluish5,
    borderRadius: 8,
    flexShrink: 0,
    marginRight: spacing(2),
    width: 48,
    height: 48,
  },
  coverImage: {
    borderRadius: 8,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  statusWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing(2),
  },
  badge: {
    flexFlow: 'row-reverse',
    padding: spacing(0, 1.5),
    height: 22,
  },
  badgeIcon: {
    marginLeft: 0,
    marginRight: spacing(1),
    marginTop: spacing(-0.5),
  },
  tagCount: {
    fontSize: 14,
    lineHeight: '18px',
  },
}));

const CourseMiniCard = ({
  className = undefined,
  translations,
  course = {},
  statuses,
  onStatusChange,
  onCardClick,
}) => {
  const classes = useStyles();
  const { title, description, coverImage, enrollmentStatus, tags } = course;
  const hasTags = !isArrayEmpty(tags);

  const handleCardClick = useCallback(() => {
    onCardClick(course.id);
  }, [course, onCardClick]);

  const handleStatusChange = useCallback(
    statusId => {
      onStatusChange(course.id, statusId);
    },
    [course, onStatusChange]
  );

  const getVirtualTagItemSize = useCallback(
    (index, items) => (index === items.length - 1 ? 20 : 28),
    []
  );

  const renderTag = tag => (
    <Tag tag={tag} color={tag?.category?.color} isSelected />
  );

  return (
    <div
      className={classNames(classes.root, className)}
      onClick={handleCardClick}
    >
      <div className={classes.statusWrapper}>
        <StatusIndicator
          status={enrollmentStatus}
          statuses={statuses}
          onSelect={handleStatusChange}
          isSelectable
        />
        {hasTags && (
          <AsyncListBadge
            badgeClass={classes.badge}
            badgeIconClass={classes.badgeIcon}
            countClass={classes.tagCount}
            tooltipTitle={translations.tags}
            count={tags.length}
            items={tags}
            icon={TagsIcon}
            getItemSize={getVirtualTagItemSize}
            renderItem={renderTag}
          />
        )}
      </div>
      <div className={classes.titleWrapper}>
        <div className={classes.coverWrapper}>
          {coverImage && (
            <img
              className={classes.coverImage}
              src={coverImage}
              alt="Course cover"
            />
          )}
        </div>
        <TextBoxWithTooltip
          className={classes.verticalEllipsis}
          text={title}
          variant="subtitle1"
          isVerticalEllipsis
        />
      </div>
      {description && (
        <TextBoxWithTooltip
          className={classes.description}
          text={description}
          variant="body1"
          isVerticalEllipsis
        />
      )}
    </div>
  );
};

CourseMiniCard.propTypes = {
  className: PropTypes.string,
  translations: PropTypes.object.isRequired,
  course: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    level: PropTypes.string,
    coverImage: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.object),
  }),
  statuses: PropTypes.arrayOf(PropTypes.object).isRequired,
  onStatusChange: PropTypes.func.isRequired,
  onCardClick: PropTypes.func.isRequired,
};

export default CourseMiniCard;
